import { clsx } from 'clsx';
import { useState } from 'react';
import { LuChevronsRight, LuX } from 'react-icons/lu';
import { MdCheckCircle, MdError, MdInfo, MdNote, MdWarning } from 'react-icons/md';

import { Alert, AlertDescription, AlertTitle } from '~components/ui/alert';
import { Button } from '~components/ui/button';

type ErrorMessageProps = {
  /** Additional CSS classes for the notification root element */
  className?: string;
  /** Whether the notification can be collapsed */
  collapsible?: boolean;
  /** The intent of the notification */
  intent?: 'info' | 'success' | 'warning' | 'error' | 'neutral' | null | undefined;
  /** The error message to display */
  message: string;
  /** Optional title for the notification */
  title?: string;
  /** Optional action handler for the notification button */
  handleAction?: () => void;
};

const INTENT_ICON = {
  info: MdInfo,
  success: MdCheckCircle,
  warning: MdWarning,
  error: MdError,
  neutral: MdNote,
} as const;

const ErrorMessage = ({
  intent = 'info',
  message,
  title,
  className,
  collapsible = false,
  handleAction,
}: ErrorMessageProps) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const alertIntent = intent ?? 'neutral';
  const IntentIcon = INTENT_ICON[alertIntent];

  const handleCollapse = () => {
    const newValue = !collapsed;
    setCollapsed(newValue);
  };

  return (
    <Alert className={clsx('@container-normal', className)} intent={alertIntent}>
      <IntentIcon className="size-4 shrink-0" aria-hidden />
      <div className="col-start-2 flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          {title && <AlertTitle>{title}</AlertTitle>}
          {!collapsed && <AlertDescription>{message}</AlertDescription>}
        </div>
        {collapsible && (
          <Button
            icon
            size="xs"
            variant="ghost"
            type="button"
            onClick={handleCollapse}
            aria-label={collapsed ? 'Expand message' : 'Collapse message'}
          >
            {collapsed ? <LuChevronsRight /> : <LuX />}
          </Button>
        )}
      </div>
      {!collapsed && handleAction && (
        <div className="col-start-2 mt-2">
          <Button className="bg-transparent" size="sm" variant="outline" type="button" onClick={handleAction}>
            Retry
          </Button>
        </div>
      )}
    </Alert>
  );
};

export { ErrorMessage };
